import { RequirementFriendlyDescription } from "../../requirementFriendlyDescription.js";
import { monitoringFormPart1DataSchema } from "../../schemas/136/monitoringForm.js";
export const monitoringFormPart1Template = {
    signing: {
        userOrTeamRequirement: {
            accreditation: [],
            // any user can do this form
            teamType: ["amhp", "mha", "ward", "independent", "guest", "generic"],
            friendlyDescription: RequirementFriendlyDescription.responsibleClinician,
        },
    },
    dataSchema: monitoringFormPart1DataSchema,
    formBuilderFields: [
        /*
        PERSON DETAILS
        */
        { type: "label", labelType: "title", label: ["Person details"] },
        {
            type: "readonly",
            label: "Patient Information: Name, Address, and Date of Birth",
            textFn: (data) => [
                data.patient?.name || "",
                [data.patient?.address, data.patient?.postalCode].join(", "),
                data.patient?.dateOfBirth ?? "Unknown date of birth",
            ],
            isReadOnlyPatientInfo: true,
            canGuestUserEdit: true,
        },
        {
            type: "branch",
            field: "accommodationStatus",
            branches: [
                {
                    fieldValue: true,
                    hideLabel: true,
                    label: "accommodationStatus",
                    fields: [
                        {
                            type: "dropdown",
                            label: "Accommodation Status (optional)",
                            valueField: "updateAccommodationStatus",
                            textField: "updateAccommodationStatusText",
                            isMultiSelect: true,
                            options: [
                                {
                                    value: "ownerOccupier",
                                    label: "Owner Occupier",
                                },
                                {
                                    value: "tenant",
                                    label: "Tenant",
                                },
                                {
                                    value: "livingWithFamilyOrFriends",
                                    label: "Living with family or friends ",
                                },
                                {
                                    value: "universityOrCollege",
                                    label: "University or College",
                                },
                                {
                                    value: "livingInCareHome",
                                    label: "Care Home",
                                },
                                {
                                    value: "homeless",
                                    label: "Homeless",
                                },
                                {
                                    value: "temporaryHousing",
                                    label: "Temporary housing",
                                },
                                {
                                    value: "admittedPatient",
                                    label: "Admitted patient",
                                },
                                {
                                    value: "criminalJusticeSettings",
                                    label: "Criminal justice settings ",
                                },
                                {
                                    value: "other",
                                    label: "Other (please give details)",
                                },
                                {
                                    value: "unknown",
                                    label: "Unknown",
                                },
                            ],
                            fieldsOwned: ["descriptionOfAccommodationStatus"],
                        },
                    ],
                    fieldsOwned: [],
                },
            ],
        },
        {
            type: "branch",
            field: "updateAccommodationStatus",
            branches: [
                {
                    hideLabel: true,
                    fieldValue: "other",
                    label: "other",
                    fields: [
                        {
                            type: "textbox",
                            rows: "5",
                            field: "descriptionOfAccommodationStatus",
                            label: "Please describe the applicable accommodation status",
                        },
                    ],
                    fieldsOwned: [],
                },
            ],
        },
        {
            type: "branch",
            label: "Is the person known to Mental Health Services? ",
            field: "personKnownMentalHealthServices",
            branches: [
                {
                    fieldValue: "true",
                    label: "Yes",
                    fieldsOwned: [],
                    fields: [],
                },
                {
                    fieldValue: "false",
                    label: "No",
                    fieldsOwned: [],
                    fields: [],
                },
                {
                    fieldValue: "unknown",
                    label: "Unknown",
                    fieldsOwned: [],
                    fields: [],
                },
            ],
        },
        {
            type: "dropdown",
            valueField: "nearestRelativeContactedValue",
            textField: "nearestRelativeContactedText",
            options: [
                {
                    label: "Yes",
                    value: "yes",
                },
                {
                    label: "No",
                    value: "no",
                },
                {
                    label: "Attempt made, no contact",
                    value: "attemptMadeNoContact",
                },
                {
                    label: "No nearest relative to my knowledge",
                    value: "noNearestRelativeToMyKnowledge",
                },
                {
                    label: "Unknown",
                    value: "unknown",
                },
            ],
            label: "Has the nearest relative been contacted?",
        },
        { type: "separator" },
        /*
        AMHP details
        */
        { type: "label", labelType: "title", label: ["AMHP details (optional)"] },
        {
            type: "date-time",
            field: "dateTimeAmhpContacted",
            label: "Date and time AMHP was contacted",
            maximum: (now) => now,
        },
        {
            type: "date-time",
            field: "dateTimeAmhpArrived",
            label: "Date and time AMHP arrived",
            maximum: (now) => now,
        },
        { type: "separator" },
        /*
        FIRST DOCTOR DETAILS
        */
        {
            type: "label",
            labelType: "title",
            label: ["First doctor details (optional)"],
        },
        {
            type: "date-time",
            field: "dateTimeFirstDoctorContacted",
            label: "Date and time first doctor was contacted",
            maximum: (now) => now,
        },
        {
            type: "date-time",
            field: "dateTimeFirstDoctorArrived",
            label: "Date and time first doctor arrived",
            maximum: (now) => now,
        },
        { type: "separator" },
        /*
        ASSESSMENT DETAILS
        */
        { type: "label", labelType: "title", label: ["Assessment details"] },
        {
            type: "date-time",
            field: "dateTimeAssessmentCompleted",
            label: "Date and time the assessment completed (optional)",
            maximum: (now) => now,
        },
        {
            type: "branch",
            field: "assessmentExtended",
            label: "Was the assessment extended?",
            branches: [
                {
                    fields: [
                        {
                            type: "dropdown",
                            valueField: "whyWasAssessmentExtendedValue",
                            textField: "whyWasAssessmentExtendedText",
                            label: "Why was the assessment extended",
                            options: [
                                {
                                    value: "tooMentallyDistressed",
                                    label: "Not practicable to assess within 24h - The person was too mentally distressed",
                                },
                                {
                                    value: "tooIntoxicated",
                                    label: "Not practicable to assess within 24h - The person was particularly intoxicated with alcohol or drugs and cannot co-operate with the assessment process",
                                },
                                { value: "other", label: "Other (please give reason)" },
                            ],
                        },
                        {
                            type: "branch",
                            field: "whyWasAssessmentExtendedValue",
                            branches: [
                                {
                                    fieldValue: "other",
                                    fields: [
                                        {
                                            type: "textbox",
                                            field: "whyWasAssessmentExtendedOtherDescription",
                                            label: "Please give reason",
                                        },
                                    ],
                                    fieldsOwned: ["whyWasAssessmentExtendedOtherDescription"],
                                    hideLabel: true,
                                    label: "Other",
                                },
                            ],
                        },
                    ],
                    fieldsOwned: [
                        "whyWasAssessmentExtendedValue",
                        "whyWasAssessmentExtendedText",
                        "whyWasAssessmentExtendedOtherDescription",
                    ],
                    fieldValue: true,
                    label: "Yes",
                },
                { fields: [], fieldsOwned: [], fieldValue: false, label: "No" },
            ],
        },
        {
            type: "date-time",
            field: "dateTimeDetentionEnded",
            label: "Date and time the Section 136 detention ended",
            maximum: (now) => now,
        },
        {
            type: "branch",
            field: "outcomeOfIncident",
            branches: [
                {
                    fieldValue: true,
                    hideLabel: true,
                    label: "outcomeOfIncident",
                    fields: [
                        {
                            type: "dropdown",
                            label: "Please confirm the outcome of the 136 incident",
                            valueField: "updateOutcomeOfIncident",
                            textField: "updateOutcomeOfIncidentText",
                            options: [
                                {
                                    value: "136Expired",
                                    label: "Section 136 Expired - Legal timeframe elapsed",
                                },
                                {
                                    value: "mhaAdmission",
                                    label: "Admission to a mental health unit under MHA",
                                },
                                {
                                    value: "admissionInformally",
                                    label: "Admission to a mental health unit informally",
                                },
                                {
                                    value: "mhFollowUp",
                                    label: "Discharge with mental health follow up",
                                },
                                {
                                    value: "primaryCareFollowUp",
                                    label: "Discharge with primary care follow up",
                                },
                                {
                                    value: "noFollowUp",
                                    label: "Discharge no follow up required",
                                },
                                {
                                    value: "custodyTransfer",
                                    label: "Discharged - taken to custody",
                                },
                                {
                                    value: "physicalHealthcareTransfer",
                                    label: "Discharged - transferred to physical healthcare unit",
                                },
                                {
                                    value: "other",
                                    label: "Other",
                                },
                            ],
                            fieldsOwned: ["dateTimePersonAdmitted", "descriptionOfOutcome"],
                        },
                    ],
                    fieldsOwned: [],
                },
            ],
        },
        {
            type: "branch",
            field: "updateOutcomeOfIncident",
            branches: [
                {
                    hideLabel: true,
                    fieldValue: "other",
                    label: "other",
                    fields: [
                        {
                            type: "textbox",
                            rows: "5",
                            field: "descriptionOfOutcome",
                            label: "Please describe the outcome of the 136 incident",
                        },
                    ],
                    fieldsOwned: [],
                },
                {
                    hideLabel: true,
                    fieldValue: "136Expired",
                    label: "136Expired",
                    fields: [
                        {
                            type: "date-time",
                            field: "dateTimePersonAdmitted",
                            label: "Please select date & time of when person was admitted",
                            maximum: (now) => now,
                        },
                    ],
                    fieldsOwned: [],
                },
                {
                    hideLabel: true,
                    fieldValue: "mhaAdmission",
                    label: "mhaAdmission",
                    fields: [
                        {
                            type: "date-time",
                            field: "dateTimePersonAdmitted",
                            label: "Please select date & time of when person was admitted",
                            maximum: (now) => now,
                        },
                    ],
                    fieldsOwned: [],
                },
                {
                    hideLabel: true,
                    fieldValue: "admissionInformally",
                    label: "admissionInformally",
                    fields: [
                        {
                            type: "date-time",
                            field: "dateTimePersonAdmitted",
                            label: "Please select date & time of when person was admitted",
                            maximum: (now) => now,
                        },
                    ],
                    fieldsOwned: [],
                },
            ],
        },
    ],
};
export const monitoringFormV1Base = {
    id: "mha-136-monitoring",
    category: "136",
    version: "1.0",
    status: "active",
    formName: "Outcome Form",
    section: "136",
    parts: [monitoringFormPart1Template],
    description: "",
    extractGenericData: () => {
        return {
            standalone: {},
        };
    },
};
//# sourceMappingURL=monitoringForm.js.map